// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ColorPaletteStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import fetcher from 'fetchers/Izenda.SystemSettingFetcher';
import { observable, computed } from 'mobx';
import { find, isUndefined } from 'lodash/fp';
import ColorPalette from 'logic/models/Izenda.ColorPalette';
import localizer from 'share/localization/Izenda.Localizer';

export default class ColorPaletteStore {
  @observable palettes = [];
  originalColorPalette = null;
  currentColorPalette = null;
  needMaskDefault = false;

  @computed
  get defaultColorPalette() {
    const palette = find('isDefaultTheme', this.palettes);
    return palette ? palette.name : null;
  }

  /**
   * Initial instant
   * @param {string} originalColorPalette
   * @param {boolean} needMaskDefault
   */
  init(originalColorPalette: string, needMaskDefault: boolean, defaultColorThemeLabel: string): any {
    this.originalColorPalette = this.currentColorPalette = originalColorPalette;
    this.needMaskDefault = needMaskDefault;
    return this.loadThemes(defaultColorThemeLabel);
  }

  /**
   * Load themes
   * @param {string} defaultColorThemeLabel
   */
  loadThemes(defaultColorThemeLabel: string): Promise {
    return fetcher.fetchThemes().then(result => {
      this.palettes = result.map(item => new ColorPalette(item, defaultColorThemeLabel));
      return result;
    });
  }

  /**
   * Get color palette detail by name
   * @param {string} name
   */
  getColorPaletteDetail(name: string): any {
    if (!this.palettes) {
      return null;
    }

    let matchingPalette = this.palettes.find(item => item.name === name);
    if (!matchingPalette) {
      matchingPalette = this.palettes.find(item => item.name === this.defaultColorPalette);
    }

    if (!!matchingPalette && this.needMaskDefault && matchingPalette.name === this.defaultColorPalette) {
      const defaultText = localizer.getText('COLORPALETTE_DEFAULT');
      matchingPalette.displayName = `${matchingPalette.displayName} (${defaultText})`;
    }

    this.currentColorPalette = matchingPalette.name;
    this.originalColorPalette = matchingPalette.name;
    return matchingPalette;
  }

  /**
   * Set current selected color palette
   * @param {string} name
   */
  updateSelectedPalette(name: string): void {
    this.currentColorPalette = name;
  }

  /**
   * Reset current palette to original one
   */
  resetSelectedPalette(): void {
    if (isUndefined(this.originalColorPalette)) {
      this.currentColorPalette = this.defaultColorPalette;
    } else {
      this.currentColorPalette = this.originalColorPalette;
    }
  }

  /**
   * Update original after selected ok
   * @param {string} name 
   */
  updateOriginalPalette(name: string): void {
    this.originalColorPalette = name;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ColorPaletteStore.js